import { memo, useMemo, useState } from 'react'

import {
  ArrowDropDownRounded,
  ArrowDropUpRounded,
  BuildRounded,
  CloudQueueRounded,
  InfoOutlineRounded,
  NotificationsPausedRounded,
  RecordVoiceOverRounded,
  RestartAltRounded,
  RouterRounded,
  SettingsRemoteRounded,
  SignalCellularAltRounded,
  SignalWifi3BarLockRounded,
  UsbRounded,
  VolumeUpRounded,
  WarningAmberRounded,
  WifiFindRounded,
  WifiTetheringErrorRounded,
} from '@mui/icons-material'
import { Box, Menu, MenuItem, ToggleButton, Typography } from '@mui/material'

import { useSmallScreen } from '@common/utils'

export const LAYER_STATUS = 'status'
export const LAYER_SNOOZE = 'snooze'
export const LAYER_AWS_STATUS = 'aws_status'
export const LAYER_PROVISION = 'provision'
export const LAYER_OPERATIONAL_STATE = 'op_state'
export const LAYER_CONN_GUIDANCE_MODE = 'conn_guidance_mode'
export const LAYER_RSSI = 'rssi'
export const LAYER_AP = 'ap'
export const LAYER_SSID = 'ssid'
export const LAYER_SSID_MIGRATION = 'ssid_migration'
export const LAYER_RESET_REASON = 'reset_reason'
export const LAYER_ERROR = 'error'
export const LAYER_FW = 'fw'
export const LAYER_MIC = 'mic'
export const LAYER_NOISE = 'noise'

export const LAYER_DATA = {
  [LAYER_STATUS]: { label: 'Unit Status', icon: InfoOutlineRounded },
  [LAYER_SNOOZE]: { label: 'Unit Snooze', icon: NotificationsPausedRounded },
  [LAYER_AWS_STATUS]: { label: 'AWS Status', icon: CloudQueueRounded },
  [LAYER_PROVISION]: { label: 'Provision State', icon: SettingsRemoteRounded },
  [LAYER_OPERATIONAL_STATE]: {
    label: 'Operational State',
    icon: WifiTetheringErrorRounded,
  },
  [LAYER_CONN_GUIDANCE_MODE]: {
    label: 'Connectivity Guidance Mode',
    icon: UsbRounded,
  },
  [LAYER_RSSI]: { label: 'RSSI Strength', icon: SignalCellularAltRounded },
  [LAYER_AP]: { label: 'Access Point', icon: RouterRounded },
  [LAYER_SSID]: { label: 'SSID', icon: SignalWifi3BarLockRounded },
  [LAYER_SSID_MIGRATION]: { label: 'SSID Migration Status', icon: WifiFindRounded },
  [LAYER_RESET_REASON]: { label: 'Reset Reason', icon: RestartAltRounded },
  [LAYER_ERROR]: { label: 'Error Conditions', icon: WarningAmberRounded },
  [LAYER_FW]: { label: 'Firmware', icon: BuildRounded },
  [LAYER_MIC]: { label: 'Has Microphone', icon: RecordVoiceOverRounded },
  [LAYER_NOISE]: { label: 'Noise Enabled', icon: VolumeUpRounded },
}

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {string} props.selectedLayer
 * @param {Function} props.setSelectedLayer
 * @param {boolean} props.disabled
 */
const LayerSelector = memo(({ value, onChange, disabled }) => {
  const isSmallScreen = useSmallScreen()

  const [anchorEl, setAnchorEl] = useState(null)

  const selectedLayerLabel = useMemo(() => {
    const label = LAYER_DATA[value]?.label
    if (!label) throw new Error(`Unexpected layer: ${value}`)
    return label
  }, [value])

  const SelectedLayerIcon = useMemo(() => {
    const Icon = LAYER_DATA[value]?.icon
    if (!Icon) throw new Error(`Unexpected layer: ${value}`)
    return Icon
  }, [value])

  return (
    <>
      <Menu
        id="layer-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ vertical: -6, horizontal: 'left' }}
        slotProps={{ list: { sx: { py: 0 } } }}
      >
        {Object.entries(LAYER_DATA).map(([key, data]) => {
          const Icon = data.icon

          return (
            <MenuItem
              divider
              key={key}
              selected={key === value}
              disabled={disabled}
              onClick={() => {
                onChange(key)
                setAnchorEl(null)
              }}
              sx={{ width: isSmallScreen.mobile ? anchorEl?.offsetWidth : null }}
            >
              <Icon sx={{ mr: 1.5, fontSize: 18 }} />
              {data.label}
            </MenuItem>
          )
        })}
      </Menu>

      <Box display="flex" flexDirection="column" justifyContent="space-between">
        <Typography variant="body">Layer</Typography>
        <ToggleButton
          selected={!!anchorEl}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          disabled={disabled}
          value=""
          sx={{ py: 0.7, mt: 1, fontSize: 14, color: '#363332' }}
        >
          <SelectedLayerIcon sx={{ mr: 1, fontSize: 20 }} />
          {selectedLayerLabel}
          {anchorEl ? <ArrowDropUpRounded /> : <ArrowDropDownRounded />}
        </ToggleButton>
      </Box>
    </>
  )
})

export default LayerSelector
